import { Box, Flex, Icon, Image, Tag, Text } from '@chakra-ui/react';
import { HomeIcon } from 'lucide-react';
import { FC } from 'react';
import { PropertySummary } from './types';

interface PropertySummaryCardProps {
  property: PropertySummary | null | undefined;
  /** Fallback title when the property has no street address. */
  folderName: string;
}

/**
 * Maps a raw `transactionSide` value to a human label, or `null` when the side
 * is unknown/absent (in which case the chip is hidden).
 */
const formatTransactionSide = (
  side: string | null | undefined
): string | null => {
  switch (side?.toUpperCase()) {
    case 'BUY':
      return 'Buy';
    case 'SELL':
      return 'Sell';
    default:
      return null;
  }
};

/**
 * Left-rail property header for the agent compliance review: thumbnail, the
 * full inline address as the title (street + city/state), the folder /
 * transaction name beneath it, and chips for transaction side and property
 * type. Falls back to the folder name as the title when there is no address.
 */
export const PropertySummaryCard: FC<PropertySummaryCardProps> = ({
  property,
  folderName,
}) => {
  const fullAddress = [
    property?.streetAddress?.trim(),
    property?.cityState?.trim(),
  ]
    .filter(Boolean)
    .join(', ');
  const title = fullAddress || folderName;
  // Show the folder/transaction name beneath the address. Skipped when there's
  // no address, since the folder name is already serving as the title.
  const subtitle = fullAddress ? folderName : undefined;
  const sideLabel = formatTransactionSide(property?.transactionSide);
  const propertyType = property?.propertyType?.trim() || null;

  return (
    <Flex align="center" gap={3}>
      {property?.imageUrl ? (
        <Image
          alt={title}
          borderRadius="md"
          boxSize="48px"
          flexShrink={0}
          objectFit="cover"
          src={property.imageUrl}
        />
      ) : (
        <Flex
          align="center"
          bg="compliance.document.bg"
          borderRadius="md"
          boxSize="48px"
          flexShrink={0}
          justify="center"
        >
          <Icon
            as={HomeIcon}
            boxSize="20px"
            color="compliance.icon.secondary"
          />
        </Flex>
      )}

      <Flex direction="column" gap={1} overflow="hidden">
        <Box overflow="hidden">
          <Text
            color="compliance.text.primary"
            fontSize="md"
            fontWeight="semibold"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {title}
          </Text>
          {subtitle && (
            <Text color="compliance.text.muted" fontSize="sm">
              {subtitle}
            </Text>
          )}
        </Box>

        {(sideLabel || propertyType) && (
          <Flex gap={2} wrap="wrap">
            {sideLabel && (
              <Tag
                bg="compliance.tag.bg"
                borderRadius="full"
                color="compliance.tag.text"
                size="sm"
              >
                {sideLabel}
              </Tag>
            )}
            {propertyType && (
              <Tag
                bg="compliance.tag.bg"
                borderRadius="full"
                color="compliance.tag.text"
                size="sm"
              >
                {propertyType}
              </Tag>
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
